import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Clock, ImageSquare, PaperPlaneRight } from "@phosphor-icons/react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LOGO_MAX = 2 * 1024 * 1024;
const BTN = "border-2 border-black px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40 transition-colors duration-100";

export const logoRequestSrc = (id) => `${BACKEND}/api/buyer-logo-requests/${id}/image`;

export function LogoBox({ src, label, testId }) {
  return (
    <div>
      <div className="label-mono mb-1">{label}</div>
      <div className="flex h-14 w-28 items-center justify-center overflow-hidden border border-black bg-zinc-900">
        {src ? <img src={src} alt={label} className="max-h-12 max-w-[104px] object-contain" data-testid={testId} />
          : <span className="text-[9px] uppercase tracking-widest text-zinc-400">No logo</span>}
      </div>
    </div>
  );
}

// Buyer dashboard: upload a new logo as a request; it shows on their pages only after an admin approves it.
export function BuyerLogoRequest({ logo, onSent }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const pick = (f) => {
    if (!f) return;
    if (f.size > LOGO_MAX) { toast.error("Logo must be 2 MB or smaller"); return; }
    setFile(f); setPreview(URL.createObjectURL(f));
  };
  const reset = () => { setFile(null); setPreview(null); setNote(""); if (ref.current) ref.current.value = ""; };
  const send = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("note", note.trim());
    setBusy(true);
    try {
      await api.post("/buyer/logo-requests", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Sent. Your new logo shows once it's approved.");
      reset(); onSent();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send your logo");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 border-t-2 border-dashed border-zinc-300 pt-4" data-testid="buyer-logo-request">
      <div className="flex flex-wrap items-end gap-4">
        <LogoBox src={logo.current_url ? `${BACKEND}${logo.current_url}` : null} label="Your logo now" testId="buyer-logo-current" />
        {logo.pending && !file && (
          <div className="flex items-end gap-2" data-testid="buyer-logo-pending">
            <LogoBox src={logoRequestSrc(logo.pending.id)} label="Waiting for approval" testId="buyer-logo-pending-img" />
            <span className="mb-1 inline-flex items-center gap-1 text-[10px] text-amber-700"><Clock size={11} weight="bold" /> sent {new Date(logo.pending.submitted_at).toLocaleDateString()}</span>
          </div>
        )}
        {preview && <LogoBox src={preview} label="New logo" testId="buyer-logo-new-preview" />}
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
               onChange={(e) => pick(e.target.files?.[0])} data-testid="buyer-logo-input" />
        {!file && (
          <button type="button" disabled={!logo.can_request} onClick={() => ref.current?.click()} className={`${BTN} hover:bg-black hover:text-white`} data-testid="buyer-logo-request-btn">
            <ImageSquare size={11} weight="bold" /> Request a new logo
          </button>
        )}
      </div>
      {file && (
        <div className="mt-3 space-y-2">
          <textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for us (optional)"
                    className="w-full border border-black px-3 py-2 text-sm" data-testid="buyer-logo-note" />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={send} disabled={busy} className={`${BTN} bg-black text-white`} data-testid="buyer-logo-send">
              <PaperPlaneRight size={11} weight="bold" /> {busy ? "Sending…" : "Send for approval"}
            </button>
            <button type="button" onClick={reset} className={`${BTN} hover:bg-black hover:text-white`} data-testid="buyer-logo-cancel">Cancel</button>
          </div>
        </div>
      )}
      <div className="mt-2 text-[10px] text-zinc-500">
        PNG, JPG or WEBP up to 2 MB. Your current logo stays until we approve the new one.
        {logo.pending && " Sending another one replaces the logo waiting for approval."}
      </div>
    </div>
  );
}
